import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Sparkles, Heart, Award, TrendingUp } from "lucide-react";

const France = () => {
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "European Markets", url: "/european-markets" },
    { name: "France", url: "/markets/france" }
  ];

  return (
    <>
      <SEO
        title="Toilettes Intelligentes & Baignoires Luxe | Aureum VII"
        description="Toilettes intelligentes REM7, baignoires îlot ARIA et spas REM7 Hydra pour promoteurs, hôtels et architectes en France. CE, livraison UE."
        keywords="toilette intelligente France, WC intelligent France, bidet intelligent, toilette luxe France, salle de bain intelligente, technologie sanitaire France"
        breadcrumbs={breadcrumbs}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Toilette Intelligente France",
          "description": "Systèmes de toilettes intelligentes premium pour le marché français",
          "url": "https://www.aureum7trading.com/markets/france"
        }}
      />
      <Navigation />
      
      <main className="min-h-screen bg-gradient-to-b from-background to-background/95">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
                  <span className="text-2xl">🇫🇷</span>
                  <span className="text-sm font-medium">Élégance Française</span>
                </div>
                
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  Toilette Intelligente France
                </h1>
                
                <p className="text-xl text-muted-foreground mb-8">
                  L'alliance parfaite entre la tradition du bidet français et l'innovation technologique. 
                  Confort, hygiène et sophistication pour votre salle de bain.
                </p>

                <div className="flex items-center gap-2 px-6 py-3 bg-card rounded-lg border mb-8 inline-flex">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <span className="font-semibold">2,500-4,000</span>
                  <span className="text-muted-foreground">recherches mensuelles</span>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg">
                    <LocalizedLink to="/contact">Demander un Devis</LocalizedLink>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <LocalizedLink to="/smart-toilets">Voir la Collection</LocalizedLink>
                  </Button>
                </div>
              </div>

              <div className="relative">
                <Card className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Heart className="w-8 h-8 text-primary" />
                    <h3 className="text-2xl font-bold">Tradition & Innovation</h3>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Technologie Bidet Intégrée</p>
                        <p className="text-sm text-muted-foreground">Respect de la culture française d'hygiène</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Design Raffiné</p>
                        <p className="text-sm text-muted-foreground">Élégance et sophistication françaises</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Heart className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Confort Premium</p>
                        <p className="text-sm text-muted-foreground">Expérience luxueuse au quotidien</p>
                      </div>
                    </li>
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Cultural Context Section */}
        <section className="py-20 px-4 bg-card/30">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Pourquoi la Toilette Intelligente en France ?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Heart className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Culture du Bidet</h3>
                <p className="text-muted-foreground">
                  Intégration naturelle dans la tradition française d'hygiène, 
                  combinant les avantages du bidet avec la technologie moderne
                </p>
              </Card>

              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Sophistication</h3>
                <p className="text-muted-foreground">
                  Design élégant qui s'harmonise avec l'esthétique raffinée 
                  des salles de bain françaises contemporaines
                </p>
              </Card>

              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Award className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Qualité Supérieure</h3>
                <p className="text-muted-foreground">
                  Matériaux premium et finitions impeccables répondant 
                  aux standards élevés du marché français
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-12">
              Questions Fréquentes sur les Toilettes Intelligentes
            </h2>
            
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Comment fonctionne le système de bidet intégré ?</h3>
                <p className="text-muted-foreground">
                  Notre système de bidet intelligent utilise une technologie de jet d'eau précise 
                  avec température et pression réglables. Il offre tous les avantages d'un bidet 
                  traditionnel français tout en économisant de l'espace dans votre salle de bain.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Est-ce adapté aux normes françaises ?</h3>
                <p className="text-muted-foreground">
                  Absolument. Toutes nos toilettes intelligentes sont conformes aux normes NF 
                  françaises et aux réglementations européennes EN. L'installation respecte les 
                  standards de plomberie français.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Quelle économie d'eau puis-je espérer ?</h3>
                <p className="text-muted-foreground">
                  Nos systèmes permettent une réduction de la consommation d'eau jusqu'à 60% 
                  par rapport aux toilettes traditionnelles, tout en maintenant une efficacité 
                  de nettoyage optimale grâce à la technologie bidet intégrée.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Quel est le coût d'entretien ?</h3>
                <p className="text-muted-foreground">
                  L'entretien est minimal grâce aux surfaces autonettoyantes et aux systèmes 
                  de désinfection automatique. Un service annuel par nos techniciens certifiés 
                  assure une performance optimale sur le long terme.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Découvrez l'Excellence Française avec l'Innovation Intelligente
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contactez notre équipe française pour une consultation personnalisée
            </p>
            <Button asChild size="lg">
              <LocalizedLink to="/contact">Contactez-nous</LocalizedLink>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default France;